import type { LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

import { monto as comoMonto } from "@/componentes/tesoreria/dinero"

export type MontoDivisa = { divisa: string; monto: string }

/**
 * La tarjeta de un indicador: título, cifra grande y, debajo, los montos por moneda.
 *
 * ⚠ **Una sola tarjeta para todos los tableros.** Nació dentro de `TableroOrdenes` y se
 * sacó aquí al aparecer el segundo y el tercero: con una copia por pantalla, el día que
 * se corrige el contraste de la cifra o el grosor del filo se corrige en una y las otras
 * se quedan viejas — que es exactamente lo que la regla del cristal evita con las
 * superficies.
 *
 * ⚠ **Sin `onClick` NO es un botón.** Una tarjeta que se levanta al pasar el ratón y no
 * hace nada promete una acción que no existe; con manejador es un filtro y lleva
 * `aria-pressed`, sin él es texto.
 *
 * ⚠ **Los montos van POR MONEDA, uno debajo de otro, y nunca hay «total».** Sumar
 * bolívares con dólares da una cifra en una unidad que no existe.
 *
 * ⚠ **El color va en el filo de la izquierda y en el icono, nunca en las cifras.** El
 * texto se pinta con tinta de texto: el color dice de qué es la tarjeta, no si la cifra
 * es buena o mala.
 */
export function TarjetaKpi({
  titulo,
  detalle,
  Icono,
  color,
  cantidad,
  montos,
  pie,
  activa,
  onClick,
  accion,
}: {
  titulo: string
  detalle: string
  Icono: LucideIcon
  color: string
  /** La cifra grande. Admite nodo para poder escribir «5 de 7» o una unidad pequeña. */
  cantidad: ReactNode
  /** Por moneda. Omitido, no se pinta la zona; vacío, un guion. */
  montos?: MontoDivisa[]
  /** Una línea extra debajo de los montos, para lo que la cifra no explica sola. */
  pie?: ReactNode
  activa?: boolean
  onClick?: () => void
  /**
   * Un control propio DENTRO de la tarjeta —un selector de tramo— (12/10), arriba a la
   * derecha y AL LADO del icono, pedido por el usuario: «7D 15D 30D», sin aspecto de
   * botones.
   *
   * ⚠ **No va dentro del botón**: un botón dentro de otro no es válido y el teclado no
   * sabría cuál se pulsa. La tarjeta reserva su hueco junto al icono y el control se
   * coloca encima como HERMANO, así que sus clics no abren el detalle de la tarjeta.
   */
  accion?: ReactNode
}) {
  // Lo que «Exportar» lee de la tarjeta (`componentes/exportar/totales.ts`).
  const kpi = { "data-exportar-kpi": "", "data-kpi-titulo": titulo, "data-kpi-detalle": detalle }
  const cuerpo = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className={accion !== undefined ? "pr-24" : undefined}>
          <div className="text-sm font-semibold">{titulo}</div>
          <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
            {detalle}
          </div>
        </div>
        <Icono size={20} strokeWidth={1.5} style={{ color }} aria-hidden="true" />
      </div>
      <div className="text-2xl font-semibold tabular-nums" data-kpi-cantidad="">
        {cantidad}
      </div>
      {montos !== undefined && (
        <div className="space-y-0.5 text-sm tabular-nums">
          {montos.length === 0 ? (
            <span style={{ color: "var(--fg-muted)" }}>—</span>
          ) : (
            montos.map((m) => (
              <div
                key={m.divisa}
                className="flex justify-between gap-2"
                data-kpi-monto={m.monto}
                data-kpi-moneda={m.divisa}
              >
                <span style={{ color: "var(--fg-muted)" }}>{m.divisa}</span>
                <span>{comoMonto(m.monto)}</span>
              </div>
            ))
          )}
        </div>
      )}
      {pie !== undefined && (
        <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
          {pie}
        </div>
      )}
    </>
  )

  if (onClick === undefined) {
    return (
      <div
        {...kpi}
        className="tarjeta relative space-y-2 p-3"
        style={{ borderLeft: `3px solid ${color}` }}
      >
        {cuerpo}
        {accion !== undefined && <div className="absolute top-2.5 right-9">{accion}</div>}
      </div>
    )
  }

  const boton = (
    <button
      {...kpi}
      type="button"
      aria-pressed={activa ?? false}
      onClick={onClick}
      className={`tarjeta tarjeta-interactiva foco-cristal space-y-2 p-3 text-left ${activa ? "activo-cristal" : ""}`}
      style={{ borderLeft: `3px solid ${color}` }}
    >
      {cuerpo}
    </button>
  )
  if (accion === undefined) return boton
  return (
    <div className="relative grid">
      {boton}
      <div className="absolute top-2.5 right-9">{accion}</div>
    </div>
  )
}
